import { ConstantField } from "@/constant/ConstantField";
import { router } from "@/router";
import { BaseRequest, RequestType } from "@/typeDefine/HttpRequestType";
import { BaseResponse } from "@/typeDefine/HttpResponseType";
import AES from "@/util/AES";
import { UtilMethods } from "@/util/UtilMethods";
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";

export class Requests {
    public static readonly instance: Requests = new Requests()

    private axiosInstance: AxiosInstance = axios.create(
        {
            baseURL: `${ConstantField.SERVER}`,
            timeout: ConstantField.TIMEOUT,
            withCredentials: true,
        }
    )

    private constructor() {
        this.init()
    }

    private init(): void {
        this.axiosInstance.interceptors.request.use(this.requestUseSuccessHandler, this.requestUseErrorHandler)

        this.axiosInstance.interceptors.response.use(this.responseUseSuccessHandler, this.responseUseErrorHandler)
    }

    private requestUseSuccessHandler(request: InternalAxiosRequestConfig<any>): InternalAxiosRequestConfig<any> {
        //对数据进行一层加密
        const key: string = AES.generateBase64Key()
        const iv: string = AES.generateBase64IV()

        //request.data 一定是BaseRequest类型
        const temp: BaseRequest = request.data

        for (const k in temp) {
            temp[k] = AES.encrypt(temp[k], key, iv)
        }

        request.data = {
            ...temp,
            key,
            iv
        }

        return request
    }

    private requestUseErrorHandler(error: any): void {

    }

    private responseUseSuccessHandler(response: AxiosResponse<any, any>): AxiosResponse<any, any> {
        const { data, ...rest } = response
        if (!response) {
            return {
                data: '接收响应失败',
                ...rest,
            }
        }

        if (!response.data) {
            return response
        }

        const responseData = response.data['data']
        if (!responseData || !response.data['data'][AES.key] || !response.data['data'][AES.iv]) {
            return response
        }

        //进行解密操作
        const key: string = responseData[AES.key]
        const iv: string = responseData[AES.iv]

        for (const k in responseData) {
            if (k !== AES.key && k !== AES.iv) {
                responseData[k] = JSON.parse(AES.decrypt(responseData[k], key, iv))
            }
        }

        return response
    }

    private responseUseErrorHandler(error: AxiosError): void {

        if (!error.status) {
            router.replace({
                name: 'index'
            })
            return
        }

        const response: BaseResponse = UtilMethods.axiosResponseStatusInfo(error.status)
        ElMessage({
            message: response.message,
            type: 'error',
            onClose() {
                router.replace({
                    name: 'index'
                })
            }
        })
    }

    //处理get请求
    public get<T>(request: RequestType): Promise<T> {
        return new Promise<T>(async (resolve, reject) => {
            try {
                //对get请求参数进行处理
                let param = ''
                if (typeof request.param === "object") {
                    param += '?'
                    for (const key in request.param) {
                        if(typeof request.param[key] !== 'undefined') {
                            param += `${key}=${request.param[key]}&`
                        }
                    }
                    param = param.slice(0, -1)
                }
                
                const response: AxiosResponse<T, any> = await this.axiosInstance.get(`${request.url}${param}`, { headers: request.header })

                if (!response) {
                    ElMessage({
                        type: 'error',
                        message: '请求失败 Request Error'
                    })
                    return
                }

                resolve(response.data)
            } catch (e) {
                reject(e)
            }
        })
    }

    //处理post请求
    public post<T>(request: RequestType): Promise<T> {
        return new Promise<T>(async (resolve, reject) => {
            try {
                const response: AxiosResponse<T, any> = await this.axiosInstance.post<T>(request.url, request.param, { headers: request.header })

                if (!response) {
                    ElMessage({
                        type: 'error',
                        message: '请求失败 Request Error'
                    })
                    return
                }

                resolve(response.data)
            } catch (e) {
                reject(e)
            }
        })
    }

    //处理put请求
    public put<T>(request: RequestType): Promise<T> {
        return new Promise<T>(async (resolve, reject) => {
            try {
                const response: AxiosResponse<T, any> = await this.axiosInstance.put<T>(request.url, request.param, { headers: request.header })

                if (!response) {
                    ElMessage({
                        type: 'error',
                        message: '请求失败 Request Error'
                    })
                    return
                }

                resolve(response.data)
            } catch (e) {
                reject(e)
            }
        })
    }

    //处理delete请求
    public delete<T>(request: RequestType): Promise<T> {
        return new Promise<T>(async (resolve,reject) => {
            try {
                let param = ''
                if(typeof request.param === 'object') {
                    for(const key in request.param) {
                        param += '/'
                        if(typeof request.param[key] !== 'undefined') {
                            param += request.param[key] + '/'
                        }
                    }
                    param = param.slice(0, -1)
                }

                const response: AxiosResponse<T, any> = await this.axiosInstance.delete(`${request.url}${param}`, {headers: request.header})

                if (!response) {
                    ElMessage({
                        type: 'error',
                        message: '请求失败 Request Error'
                    })
                    return
                }

                resolve(response.data)
            }catch(e) {
                reject(e)
            }
        })
    }
}